import { FC, lazy, memo, Suspense, useMemo } from "react";
import type { ImgHTMLAttributes } from "react";
import { Link } from "react-router-dom";
import { PageRoutesConstant } from "../../common/components/router/components/PageRoutes";
import SeoHelmet from "../../common/components/seo/SeoHelmet";
import {
  generateOrganizationSchema,
  generateWebSiteSchema,
  generateServiceSchema,
} from "../../common/utils/structuredData";
import { getServiceCountries } from "../../common/constants/countries";
import { TestimonialData } from "../testimonials/Testimonial.constant";
import heroBg from "../../assets/images/hero-bg-tech.webp";
import "./Home.style.scss";

const Services = lazy(() => import("../services/Services"));
const About = lazy(() => import("../about/About"));
const Contact = lazy(() => import("../contact/Contact"));
const Testimonial = lazy(() => import("../testimonials/Testimonial"));
const WorkWithYou = lazy(() => import("../work-with-you/WorkWithYou"));

const Home: FC = () => {
  const siteName = process.env.REACT_APP_SITE_NAME || "BIGELEMENT INFOTECH";
  const siteUrl = process.env.REACT_APP_SITE_URL || "";
  const serviceCountries = getServiceCountries();

  const structuredData = useMemo(() => {
    const orgDescription =
      "Leading web development, product development, and UI/UX design company. We build websites, web applications, software products, and user-centered digital experiences.";
    const organizationSchema = generateOrganizationSchema({
      name: siteName,
      url: siteUrl,
      logo: `${siteUrl}/assets/images/beit_logo.svg`,
      description: orgDescription,
      contactPoint: {
        contactType: "Customer Service",
        areaServed: serviceCountries,
      },
      areaServed: serviceCountries,
      address: {
        addressCountry: "IN",
        addressLocality: "India",
      },
      aggregateRating: {
        ratingValue: 5,
        reviewCount: TestimonialData.length,
        bestRating: 5,
        worstRating: 1,
      },
      reviews: TestimonialData.slice(0, 5).map((t) => ({
        author: t.name,
        reviewBody: t.description,
        reviewRating: { ratingValue: t.rating, bestRating: 5, worstRating: 1 },
      })),
    });

    const websiteSchema = generateWebSiteSchema(
      siteUrl,
      siteName,
      orgDescription,
    );

    const serviceSchemas = [
      {
        name: "Web Development",
        description:
          "Build high-performance websites and web apps that drive growth and scale with your business.",
        provider: { name: siteName, url: siteUrl },
        areaServed: serviceCountries,
        serviceType: "Web Development",
      },
      {
        name: "Product Development",
        description:
          "Transform your vision into scalable software products that streamline operations and drive growth.",
        provider: { name: siteName, url: siteUrl },
        areaServed: serviceCountries,
        serviceType: "Product Development",
      },
      {
        name: "UI/UX Development",
        description:
          "Design intuitive, conversion-focused interfaces that engage users and maximize ROI.",
        provider: { name: siteName, url: siteUrl },
        areaServed: serviceCountries,
        serviceType: "UI/UX Design",
      },
    ].map((s) => generateServiceSchema(s));

    return [organizationSchema, websiteSchema, ...serviceSchemas];
  }, [siteName, siteUrl, serviceCountries]);

  return (
    <>
      <SeoHelmet
        pageTitle="Web Development, Product & UI/UX Design Company"
        pageDescription="Your trusted partner in digitization. Web development, product development & UI/UX design. Get a free quote or consultation—India, USA, UK, Canada, UAE, Australia."
        pageUrl={PageRoutesConstant.Home.path}
        pageKeywords="web development company India, web development USA, UI/UX design agency, product development services, custom software solutions, hire web developers, get a quote, digital transformation"
        ogImage={`${siteUrl}/assets/images/hero-bg-tech.webp`}
        preloadImage={heroBg}
        structuredData={structuredData}
      />
      <div className="home">
        <HomeBanner />
        <div className="home-section">
          <Suspense fallback={<div className="suspense-fallback" />}>
            <About embeddedInHome />
          </Suspense>
        </div>
        <div className="home-section">
          <Suspense fallback={<div className="suspense-fallback" />}>
            <WorkWithYou
              showExpertise={false}
              showReadyToStartProject={false}
            />
          </Suspense>
        </div>
        <div id="services" className="home-section">
          <Suspense fallback={<div className="suspense-fallback" />}>
            <Services />
          </Suspense>
        </div>
        <div className="home-section">
          <Suspense fallback={<div className="suspense-fallback" />}>
            <Testimonial />
          </Suspense>
        </div>
        {/* <Client /> */}
        <div className="home-section">
          <Suspense fallback={<div className="suspense-fallback" />}>
            <Contact />
          </Suspense>
        </div>
      </div>
    </>
  );
};

const siteName = process.env.REACT_APP_SITE_NAME || "BIGELEMENT INFOTECH";

const scrollToServices = () => {
  document.getElementById("services")?.scrollIntoView({ behavior: "smooth" });
};

const HomeBanner: FC = memo(function HomeBanner() {
  return (
    <section
      className="home_banner"
      aria-label="Hero - Web Development and Digital Solutions"
    >
      <img
        src={heroBg}
        srcSet={`${heroBg} 1536w`}
        sizes="100vw"
        alt="BIGELEMENT INFOTECH - Web Development, Product Development and UI/UX Design. Your partner in digitization."
        className="home_banner_image"
        loading="eager"
        decoding="async"
        {...({ fetchpriority: "high" } as ImgHTMLAttributes<HTMLImageElement>)}
        width={1536}
        height={864}
        style={{ aspectRatio: "1536 / 864" }}
      />
      <div className="home_banner_overlay" aria-hidden="true" />
      <div className="home_banner_wrapper container">
        <p className="home_banner_wrapper_eyebrow">Welcome to</p>
        <h1 className="home_banner_wrapper_company">{siteName}</h1>
        <p className="home_banner_wrapper_description">
          Your Partner in Digitization
        </p>
        <div className="home_banner_wrapper_ctas">
          <div className="home_banner_wrapper_cta_slot">
            <Link
              to={PageRoutesConstant.Contact.path}
              className="home_banner_wrapper_cta_primary"
              aria-label="Get in touch - go to contact page"
            >
              Get in Touch
            </Link>
          </div>
          <button
            type="button"
            className="home_banner_wrapper_cta_secondary"
            onClick={scrollToServices}
          >
            Our Services
          </button>
        </div>
      </div>
    </section>
  );
});

export default Home;
